/**
 * Debounced file watcher orchestration for git workspaces.
 */
import { listen } from '@tauri-apps/api/event';
import { invoke } from '@tauri-apps/api/core';
import { isDirty } from '../utils/yamlWorkspace/conflict.js';
import { makeSafeUnlisten } from '../utils/safeTauriEvent';
import { isOwnEcho } from './gitOwnWrites.js';
import { MANAGED_ROOTS, MANAGED_TOP_FILES } from './gitFileWriter.js';

/**
 * Only workspace DATA reaches the app: apiboo.yaml / .gitignore and *.yaml
 * under the managed roots. A README, the user's code next to it, or git's own
 * .git/ bookkeeping changing must not reload and re-apply the whole workspace
 * (git sync Stage 1, card 4).
 */
export function isWorkspaceDataPath(relativePath) {
  const rel = String(relativePath || '').replace(/\\/g, '/');
  if (MANAGED_TOP_FILES.has(rel)) return true;
  const top = rel.split('/')[0];
  return MANAGED_ROOTS.includes(top) && rel.endsWith('.yaml');
}

/** The file's current text, or null when it is gone. */
async function readTextOrNull(path) {
  try {
    const fs = await import('@tauri-apps/plugin-fs');
    return await fs.readTextFile(path);
  } catch {
    return null;
  }
}

/** @type {Map<string, Function>} */
const reloadCallbacks = new Map();

let unlistenFn = null;

/**
 * Tauri's unlisten throws ("listeners[eventId].handlerId" is undefined) if the
 * listener was already torn down or not yet registered webview-side. The
 * shared helper swallows both the sync throw and the async rejection (the
 * local try/catch this used to have missed the latter — unlisten is async).
 */
function safeUnlisten() {
  const fn = unlistenFn;
  unlistenFn = null;
  if (typeof fn === 'function') makeSafeUnlisten(fn)();
}

export async function startWorkspaceFileWatcher(rootPath, onReload) {
  const root = String(rootPath || '').replace(/\\/g, '/').replace(/\/+$/, '');
  if (!root) return () => {};

  reloadCallbacks.set(root, onReload);

  try {
    await invoke('start_workspace_watcher', { rootPath: root });
  } catch (err) {
    console.warn('[workspaceFileWatcher] Failed to start watcher:', err);
  }

  if (!unlistenFn) {
    unlistenFn = await listen('workspace-file-changed', async (event) => {
      const { path, relativePath } = event.payload || {};
      if (!relativePath) return;

      // Skip our own writes to apiboo temp patterns
      if (relativePath.endsWith('.tmp')) return;
      if (!isWorkspaceDataPath(relativePath)) return;

      // Our own flush coming back (gitOwnWrites.js). Re-applying it would
      // overwrite SQLite with whatever the YAML does not carry — the echo the
      // git-sync audit (01.10.2026) found deleting fields seconds after a save.
      if (await isOwnEcho(path, readTextOrNull)) return;

      for (const [rootKey, cb] of reloadCallbacks.entries()) {
        const normPath = String(path || '').replace(/\\/g, '/');
        if (normPath.startsWith(rootKey)) {
          const dirty = isDirty(rootKey, relativePath);
          cb({ path: normPath, relativePath, dirty, rootPath: rootKey });
        }
      }
    });
  }

  return async () => {
    reloadCallbacks.delete(root);
    if (reloadCallbacks.size === 0) {
      try {
        await invoke('stop_workspace_watcher');
      } catch {
        // ignore
      }
      safeUnlisten();
    }
  };
}

export async function stopAllWorkspaceWatchers() {
  reloadCallbacks.clear();
  try {
    await invoke('stop_workspace_watcher');
  } catch {
    // ignore
  }
  safeUnlisten();
}
